<template>
  <div class="fuelling-estimate">
    <div v-if="loading" class="fe-loading">Loading estimate…</div>
    <div v-else-if="error" class="fe-error">{{ error }}</div>

    <template v-else-if="data">
      <!-- Setup: sex + consent (prerequisites) -->
      <div class="fe-setup">
        <div class="fe-setup-item">
          <span class="fe-label">Sex</span>
          <template v-if="data.sex">
            <span class="fe-value">{{ data.sex === 'M' ? 'Male' : 'Female' }}</span>
          </template>
          <template v-else>
            <span class="fe-missing">not set —</span>
            <button class="fe-mini-btn" :disabled="saving" @click="setGender('M')">Male</button>
            <button class="fe-mini-btn" :disabled="saving" @click="setGender('F')">Female</button>
          </template>
        </div>
        <div class="fe-setup-item">
          <span class="fe-label">Parent/guardian consent</span>
          <label class="fe-switch">
            <input type="checkbox" :checked="data.consented" :disabled="saving" @change="toggleConsent($event)" />
            <span>{{ data.consented ? 'On file' : 'Not recorded' }}</span>
          </label>
        </div>
      </div>

      <!-- Estimate (numbers are STAFF-ONLY) -->
      <div v-if="data.estimate && data.estimate.available" class="fe-estimate">
        <div class="fe-cards">
          <div class="fe-card">
            <div class="fe-card-value">{{ data.estimate.bmr }}</div>
            <div class="fe-card-label">BMR (kcal/day)</div>
          </div>
          <div class="fe-card highlight">
            <div class="fe-card-value">{{ data.estimate.today_kcal }}</div>
            <div class="fe-card-label">Today · {{ data.estimate.today_day_type === 'training' ? 'training/match' : 'rest' }}</div>
          </div>
          <div class="fe-card">
            <div class="fe-card-value">{{ data.estimate.rest_kcal }} / {{ data.estimate.training_kcal }}</div>
            <div class="fe-card-label">Rest / training</div>
          </div>
        </div>
        <p class="fe-caveat">{{ data.estimate.caveat }}</p>
      </div>
      <div v-else class="fe-unavailable">
        {{ (data.estimate && data.estimate.reason) || 'Estimate not available yet.' }}
        <span v-if="!data.weight_kg"> The player needs to log their weight in the app.</span>
      </div>

      <!-- Weight trend (staff-only) -->
      <div class="fe-section">
        <h4>Weight trend
          <span class="fe-sub">last {{ trendDays }} days · staff only</span>
        </h4>
        <div v-if="data.trend && data.trend.length" class="fe-trend">
          <div v-for="(t, i) in data.trend" :key="i" class="fe-bar-col" :title="`${t.weight_kg} kg · ${formatDate(t.logged_at)}`">
            <div class="fe-bar" :style="{ height: barHeight(t.weight_kg) }"></div>
          </div>
        </div>
        <p v-else class="fe-empty">No weight logs yet.</p>
        <p v-if="data.weight_kg" class="fe-latest">Latest: <strong>{{ data.weight_kg }} kg</strong></p>
      </div>

      <!-- Flags -->
      <div v-if="data.flags && data.flags.length" class="fe-section">
        <h4>Flags</h4>
        <div v-for="(f, i) in data.flags" :key="i" class="fe-flag" :class="f.severity">
          {{ f.message }}
        </div>
      </div>

      <p class="fe-reds">{{ data.reds_signpost }}</p>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Nutrition } from "@/services/nutrition-service";

export default {
  name: "FuellingEstimate",

  props: {
    player: { type: Object, required: true },
  },

  data() {
    return {
      loading: true,
      saving: false,
      error: null,
      data: null,
      trendDays: 90,
    };
  },

  computed: {
    ...mapGetters(["getProfile"]),

    trendRange() {
      const ws = (this.data?.trend || []).map((t) => Number(t.weight_kg));
      if (!ws.length) return { min: 0, max: 1 };
      const min = Math.min(...ws);
      const max = Math.max(...ws);
      return { min, max: max === min ? min + 1 : max };
    },
  },

  watch: {
    "player.id": "load",
  },

  mounted() {
    this.load();
  },

  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const res = await Nutrition.getPlayerFuellingEstimate(this.player.id, null, this.trendDays);
        if (res.data && res.data.success) {
          this.data = res.data.data;
        } else {
          this.error = (res.data && res.data.error) || "Failed to load estimate";
        }
      } catch (e) {
        this.error = "Failed to load estimate";
      } finally {
        this.loading = false;
      }
    },

    async setGender(g) {
      this.saving = true;
      try {
        await Nutrition.setPlayerGender(this.player.id, g);
        await this.load();
      } finally {
        this.saving = false;
      }
    },

    async toggleConsent(e) {
      const value = e.target.checked;
      this.saving = true;
      try {
        await Nutrition.setEstimatorConsent(this.player.id, value, this.getProfile?.id);
        await this.load();
      } finally {
        this.saving = false;
      }
    },

    barHeight(weight) {
      const { min, max } = this.trendRange;
      const pct = Math.round(((Number(weight) - min) / (max - min)) * 100);
      // keep a visible floor so every bar shows
      return Math.max(12, pct) + "%";
    },

    formatDate(value) {
      if (!value) return "";
      const d = new Date(String(value).replace(" ", "T"));
      return isNaN(d.getTime()) ? "" : d.toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.fuelling-estimate {
  padding: 4px 0;
}
.fe-loading,
.fe-error,
.fe-unavailable,
.fe-empty {
  padding: 16px;
  color: #6b7280;
}
.fe-error {
  color: #b91c1c;
}
.fe-setup {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 12px 14px;
  background: #f9fafb;
  border: 1px solid #eee;
  border-radius: 10px;
  margin-bottom: 16px;
}
.fe-setup-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.fe-label {
  font-size: 0.8rem;
  color: #6b7280;
  font-weight: 600;
}
.fe-value {
  font-weight: 700;
}
.fe-missing {
  color: #b45309;
  font-size: 0.85rem;
}
.fe-mini-btn {
  border: 1px solid #d1d5db;
  background: #fff;
  border-radius: 6px;
  padding: 2px 10px;
  cursor: pointer;
  font-size: 0.85rem;
}
.fe-mini-btn:hover {
  background: #f3f4f6;
}
.fe-switch {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
}
.fe-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.fe-card {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 14px;
  text-align: center;
}
.fe-card.highlight {
  border-color: #1abc9c;
  background: #f0fdfa;
}
.fe-card-value {
  font-size: 1.4rem;
  font-weight: 800;
  color: #111827;
}
.fe-card-label {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 4px;
}
.fe-caveat {
  margin-top: 10px;
  font-size: 0.78rem;
  font-style: italic;
  color: #6b7280;
}
.fe-section {
  margin-top: 20px;
}
.fe-section h4 {
  margin: 0 0 8px;
}
.fe-sub {
  font-weight: 400;
  font-size: 0.75rem;
  color: #9ca3af;
  margin-left: 6px;
}
.fe-trend {
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 80px;
  padding: 8px;
  background: #f9fafb;
  border-radius: 8px;
}
.fe-bar-col {
  flex: 1;
  height: 100%;
  display: flex;
  align-items: flex-end;
}
.fe-bar {
  width: 100%;
  background: #1abc9c;
  border-radius: 3px 3px 0 0;
  min-height: 6px;
}
.fe-latest {
  margin-top: 8px;
  font-size: 0.9rem;
}
.fe-flag {
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.85rem;
  margin-bottom: 6px;
}
.fe-flag.review {
  background: #fff7ed;
  color: #9a3412;
}
.fe-flag.concern {
  background: #fef2f2;
  color: #b91c1c;
}
.fe-reds {
  margin-top: 18px;
  font-size: 0.8rem;
  color: #6b7280;
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;
}
</style>
